import { LocationActivityRange, LocationIncomePoint } from '@app/areas/dashboard/models/activity-report.model';

/**
 * Adaptive-granularity bucketing for the per-merchant income drill-down.
 *
 * A year-scale window starts bucketed by MONTH, a month-scale by WEEK, a week by
 * DAY, and a day by HOUR. Clicking a bar drills one level finer (month -> week ->
 * day -> hour); HOUR is the finest level.
 *
 * All Dates are in local time (the API converts local<->UTC at the service edge);
 * each LocationIncomePoint is already a single raw transaction, so a bucket's
 * dollar total and transaction count come from the same matching rows.
 */
export type IncomeGranularity = 'month' | 'week' | 'day' | 'hour';

export interface IncomeLevel {
  label: string;
  granularity: IncomeGranularity;
  start: Date;
  endExclusive: Date;
}

export interface IncomeBar {
  label: string;
  amountTotal: number;
  transactionCount: number;
  start: Date;
  endExclusive: Date;
  inRange: boolean;
}

const WEEK_TIER_DAYS = 7;
const MONTH_TIER_DAYS = 31;

// --- date helpers (local time) ---------------------------------------------

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes(), d.getSeconds());
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function addHours(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 3600_000);
}
/** Whole calendar days between two dates (date-only). */
function daysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000);
}

// --- labels -----------------------------------------------------------------

const pad = (n: number) => n.toString().padStart(2, '0');
function shortLabel(d: Date): string {
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
}
function monthLabel(d: Date): string {
  return `${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}
export function hourLabel(d: Date): string {
  const h = d.getHours();
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}${h < 12 ? 'am' : 'pm'}`;
}
export function timeLabel(d: Date): string {
  const h = d.getHours();
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${pad(d.getMinutes())}${h < 12 ? 'am' : 'pm'}`;
}

// --- core -------------------------------------------------------------------

/** The starting level for a window, its granularity sized up to the next filter tier. */
export function rootLevel(from: Date, toExclusive: Date): IncomeLevel {
  const days = daysBetween(from, toExclusive);
  const granularity: IncomeGranularity = days <= WEEK_TIER_DAYS ? 'day' : days <= MONTH_TIER_DAYS ? 'week' : 'month';
  return {
    label: `${shortLabel(from)} - ${shortLabel(addDays(toExclusive, -1))}`,
    granularity,
    start: from,
    endExclusive: toExclusive,
  };
}

export function isInActivityRange(bar: IncomeBar, range: LocationActivityRange | null): boolean {
  if (!range || !range.earliest || !range.latest) {
    return true;
  }
  const earliest = new Date(range.earliest);
  const latest = new Date(range.latest);
  return bar.endExclusive > earliest && bar.start <= latest;
}

/** Bucket raw transaction points into bars for the given level. */
export function buildBars(
  points: LocationIncomePoint[],
  level: IncomeLevel,
  range: LocationActivityRange | null = null
): IncomeBar[] {
  const parsed = points.map((p) => ({ ts: new Date(p.timestamp), amount: p.amountTotal }));

  const bucket = (start: Date, endExclusive: Date): { amount: number; count: number } => {
    let amount = 0;
    let count = 0;
    for (const p of parsed) {
      if (p.ts >= start && p.ts < endExclusive) {
        amount += p.amount;
        count += 1;
      }
    }
    return { amount, count };
  };

  const bars: IncomeBar[] = [];
  const push = (label: string, start: Date, endExclusive: Date) => {
    const { amount, count } = bucket(start, endExclusive);
    const bar: IncomeBar = { label, amountTotal: amount, transactionCount: count, start, endExclusive, inRange: true };
    bar.inRange = isInActivityRange(bar, range);
    bars.push(bar);
  };

  let cursor: Date;
  switch (level.granularity) {
    case 'month':
      cursor = startOfMonth(level.start);
      while (cursor < level.endExclusive) {
        const next = addMonths(cursor, 1);
        push(monthLabel(cursor), cursor, next);
        cursor = next;
      }
      break;
    case 'week':
      cursor = level.start;
      while (cursor < level.endExclusive) {
        // Always a full 7-day week (the last one may run past the window rather than truncate).
        const next = addDays(startOfDay(cursor), 7);
        push(`${shortLabel(cursor)}-${shortLabel(addDays(next, -1))}`, cursor, next);
        cursor = next;
      }
      break;
    case 'day':
      cursor = level.start;
      while (cursor < level.endExclusive) {
        const next = addDays(startOfDay(cursor), 1);
        push(shortLabel(cursor), cursor, next);
        cursor = next;
      }
      break;
    case 'hour':
      cursor = level.start;
      while (cursor < level.endExclusive) {
        const next = addHours(cursor, 1);
        push(hourLabel(cursor), cursor, next);
        cursor = next;
      }
      break;
  }
  return bars;
}

/** The finer level reached by clicking a bar, or null at HOUR (the finest). */
export function nextLevel(level: IncomeLevel, bar: IncomeBar): IncomeLevel | null {
  const finer: Record<IncomeGranularity, IncomeGranularity | null> = {
    month: 'week',
    week: 'day',
    day: 'hour',
    hour: null,
  };
  const granularity = finer[level.granularity];
  if (!granularity) {
    return null;
  }
  return { label: bar.label, granularity, start: bar.start, endExclusive: bar.endExclusive };
}

/** A same-duration comparison window starting on `anchor` (not re-snapped to a calendar boundary). */
export function comparisonWindowFor(level: IncomeLevel, anchor: Date): { from: Date; to: Date } {
  const durationDays = daysBetween(level.start, level.endExclusive);
  const from = startOfDay(anchor);
  return { from, to: addDays(from, durationDays) };
}

/** The raw transactions falling inside a bar's window, time-sorted (for the hour breakdown). */
export function transactionsInBar(
  points: LocationIncomePoint[],
  bar: IncomeBar
): { timestamp: Date; amount: number }[] {
  return points
    .map((p) => ({ timestamp: new Date(p.timestamp), amount: p.amountTotal }))
    .filter((t) => t.timestamp >= bar.start && t.timestamp < bar.endExclusive)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
}
